import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { BASE_URL } from "../../utils/constants";

const RoomAmenities = () => {
  const [hotels, setHotels] = useState([]);
  const [amenityData, setAmenityData] = useState({
    hotel_id: "",
    amenity_name: "",
    is_active: "1",
  });

  const [amenityImages, setAmenityImages] = useState([]);

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const response = await axios.get(`${BASE_URL}Hotel`);
        if (response.data && Array.isArray(response.data.data)) {
          setHotels(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch hotels:", error);
      }
    };

    fetchHotels();
  }, []);

  const handleAmenityImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      seq_no: "",
      amenity_name: "",
    }));
    setAmenityImages((prev) => [...prev, ...newImages]);
  };

  const handleAmenitySequenceChange = (index, value) => {
    const updatedImages = [...amenityImages];
    updatedImages[index].seq_no = value;
    setAmenityImages(updatedImages);
  };

  const handleAmenityNameChange = (index, value) => {
    const updatedImages = [...amenityImages];
    updatedImages[index].amenity_name = value;
    setAmenityImages(updatedImages);
  };

  const handleRemoveAmenityImage = (index) => {
    setAmenityImages(amenityImages.filter((_, i) => i !== index));
  };

  const handleSaveAmenities = async () => {
    if (amenityImages.some((img) => !img.seq_no || !img.amenity_name)) {
      alert("Please provide sequence numbers and amenity names for all images");
      return;
    }

    const formData = new FormData();

    const amenityDataArray = amenityImages.map((img) => ({
      hotel_id: parseInt(amenityData.hotel_id),
      am_name: img.amenity_name,
      am_image_seq_no: parseInt(img.seq_no),
    }));

    formData.append("amenityDataArray", JSON.stringify(amenityDataArray));

    amenityImages.forEach((img) => {
      formData.append("images", img.file);
    });

    try {
      const response = await axios.post(
        `${BASE_URL}image_hotel_amenities/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      console.log("Amenities upload success:", response.data);
      alert("Amenities saved successfully!");
      setAmenityImages([]);
      setAmenityData({
        hotel_id: "",
        amenity_name: "",
        is_active: "1",
      });
    } catch (error) {
      console.error("Amenities upload failed:", error);
      alert(
        "Failed to save amenities: " +
          (error.response?.data?.message || error.message),
      );
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Hotel Room Amenities
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: "repeat(1, 1fr)",
          }}
        >
          <TextField
            select
            label="Hotel Name"
            value={amenityData.hotel_id}
            onChange={(e) =>
              setAmenityData({ ...amenityData, hotel_id: e.target.value })
            }
            SelectProps={{
              native: true,
            }}
            variant="outlined"
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "40px",
                "& fieldset": {
                  borderColor: "#E0E0E0",
                },
                "&:hover fieldset": {
                  borderColor: "#BDBDBD",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1976D2",
                },
              },
              "& .MuiInputLabel-root": {
                backgroundColor: "white",
                padding: "0 8px",
                marginLeft: "-4px",
              },
              "& .MuiSelect-select": {
                padding: "8px 14px",
              },
            }}
          >
            <option value="" disabled>
              Select Hotel
            </option>
            {hotels
              .filter((hotel) => hotel.is_active === true)
              .map((hotel) => (
                <option key={hotel.id} value={hotel.hotel_id}>
                  {hotel.name}
                </option>
              ))}
          </TextField>
        </Box>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleAmenityImageChange}
            />
          </Grid>
        </Grid>

        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Preview</TableCell>
                <TableCell>Amenity Name</TableCell>
                <TableCell>Sequence No</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {amenityImages.map((img, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <img
                      src={img.preview}
                      alt={`Preview ${index}`}
                      style={{
                        width: "100px",
                        height: "80px",
                        objectFit: "cover",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={img.amenity_name}
                      onChange={(e) =>
                        handleAmenityNameChange(index, e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={img.seq_no}
                      onChange={(e) =>
                        handleAmenitySequenceChange(index, e.target.value)
                      }
                      sx={{ width: "100px" }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleRemoveAmenityImage(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveAmenities}
          disabled={!amenityData.hotel_id || !amenityImages.length}
        >
          Save Amenities
        </Button>
      </Box>
    </Box>
  );
};

export default RoomAmenities;
